import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
// LAUNCH-2 — the trial's terms, on the other surface that starts a
// checkout. See config/launch.ts.
import { TRIAL_SHIPPED, TRIAL_SHORT, TRIAL_DAYS } from '@/config/launch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization, useUpdateOrganization, useCreateOrganization, OrgRecoveryCandidateError } from '@/services/organizations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCreateProject } from '@/services/projects';
import { useCreateCheckout, useLapsedSubscription } from '@/services/billing';
// FOUNDING-3 — a Founding Member who pressed "Get the Founding Pass" with
// no account lands here after sign-up; the plan step offers the Pass.
import { FOUNDING_PASS, type PassTier } from '@/config/founding';
import { passNumbers } from '@/components/founding/kit';
import {
  clearFoundingIntent,
  foundingIntentOf,
  getFoundingKit,
  PassCheckoutError,
  readFoundingIntent,
  startFoundingPass,
} from '@/services/founding';
import { PLAN_PRICING, PLAN_FEATURES } from '@/services/billing';
// P0-1 — two lies lived on this step. `TRIAL_CONFIG` promised a
// 14-day free trial "no credit card required" directly above a button
// that opens a Stripe card form; there is no trial anywhere in the
// product (see the note where TRIAL_CONFIG used to live in
// billing.ts). And Orbit Business was offered as a selectable plan
// whose checkout can only 503 until its Stripe prices exist.
import { BUSINESS_ON_SALE, BUSINESS_HELD_MESSAGE, SIGNUP_REQUIRES_PLAN } from '@/config/launch';
import { useSubscription } from '@/hooks/useSubscription';
// FREEWEEK-1 / OPEN-1 — where setup gets to (Mission Control's funnel;
// recorded only for visitors who accepted analytics).
import { track } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';
import { useShareOrigin } from '@/hooks/useShareOrigin';
import { ServiceTabsEditor } from './ServiceTabsEditor';
import { AestheticPicker } from './AestheticPicker';
import { supabase } from '@/integrations/supabase/client';
import {
  ROLE_CARDS,
  creativeRolePersistValue,
  type CreativeRole,
} from '@/lib/onboardingPersonalization';
// PASSPORT-2 — the one goal system: one sentence, rings you close.
import { useCreateGoal, type CounterMetric, type Goal as PassportGoal } from '@/services/passport';
import { worldOf } from '@/components/galaxy3d/worlds';
import { BusinessType, serviceTabPresets, ServiceTabConfig, getDefaultServiceTabs } from '@/data/serviceTabPresets';
// UX-6 — the wizard's category picks must land in the
// `inquiry_categories` table, because that table is the ONE source
// the Add-lead Service dropdown (ManualEntryModal), the pipeline
// tabs (ServiceTabs), the Inquiries page, and the public intake form
// all read from. `useCreateOrganization` / `useUpdateOrganization`
// deliberately STRIP `service_tabs` from their payloads ("the
// onboarding wizard can save categories separately if needed") — and
// until now the wizard never did, so onboarding's four categories
// evaporated and every dropdown fell back to "General Inquiry".
import { useSaveInquiryCategories, useInquiryCategories } from '@/services/inquiryCategories';
import { parseServiceTabs } from '@/lib/typeGuards';
import { debug } from '@/lib/debug';
import { syncStorage } from '@/lib/storage';
import { type NovaState } from '@/components/nova/Nova';
import { NovaExpression } from '@/components/nova/kit';
// ONB-5 — the twelve-beat setup flow. Each new screen is its own file
// under ./steps; this wizard owns the order, the state and the writes.
import { ONB5_FLOW } from '@/config/launch';
import { useCreateIntakeForm } from '@/services/intakeForms';
import { PASSPORT_PATH } from '@/config/routes';
// ONB-5b — the setup flow wears the Googie design system. Same shell
// the app runs in, so the sky, the stars, the starburst, Bricolage over
// DM Sans and every token come from ONE place and can never drift from
// the product a studio lands in when setup ends.
import { isPlausibleEmail, isPlausiblePhone } from './steps/ContactStep';
import type { FormHome } from './steps/FirstFormStep';
import { STARTER_EXPENSE_CATEGORIES } from './steps/ExpensesStep';
// CSV-IMPORT-1 — read the file here, bring clients in through RLS (or hold
// them until the plan starts: a business with no plan yet is on Free).
import {
  ClientImportError,
  MAX_IMPORT_BYTES,
  clearPendingImport,
  importClients,
  loadPendingImport,
  readClientsCsv,
  savePendingImport,
} from '@/services/clientImport';
import { FirstRunFrame } from '@/components/firstrun/Stage';
import { CRAFT, GOALS } from '@/components/firstrun/tokens';
import {
  ContactStage, ConvergeStage, CraftStage, ExpensesStage, FormStage, ImportStage, NovaStage, PlanStage,
  SitesStage, StudioStage, WelcomeAboardStage, WelcomeStage, craftOf, type StepNav,
} from './FirstRunSteps';
import { PassportComposeStage, PassportMeetStage, PassportWorldStage, goalText } from './PassportStages';

const ORBIT_TOUR_PATH = '/tour/orbit';
import { getRoleCard } from '@/lib/onboardingPersonalization';
import { NovaSpinner } from '@/components/nova';
import { ArrowRight, ArrowLeft, Check, Copy, AlertCircle, Bug } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";

interface OnboardingWizardProps {
  /**
   * ONB-5 — optionally told WHERE to land. The old flow always ended
   * on Today; this one ends in Passport, looking at the planet the
   * studio just earned. Callers that ignore the argument keep their
   * existing behaviour, which is every caller but the new one.
   */
  onComplete: (landingPath?: string) => void;
  /**
   * HOTFIX-4 — open the wizard on a specific step. ProtectedRoute
   * passes 'studio' when it force-mounts the wizard for a signed-in
   * account whose first-run flags are stamped but whose org doesn't
   * exist (the Skip-path hole), so the user lands directly on org
   * creation instead of re-seeing the intro steps. When provided it
   * wins over the resume bookmark — it's only sent when setup is
   * being enforced.
   */
  initialStepKey?: StepKey;
  /**
   * DEVMODE-1 — dry run. The real screens, the real motion, and not one
   * write: no org, no profile update, no import, no planet, no form, no
   * checkout, no step bookmark, and the self-dismiss that hides the
   * wizard from an established studio is off. Admin → Preview mounts
   * the wizard this way so the flow can be reviewed from a signed-in
   * account.
   */
  preview?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Step config — Nova is the centerpiece. Each step maps to a Nova emotional
// state so she "performs" the thing the screen is asking about.
// ─────────────────────────────────────────────────────────────────────────────

export type StepKey =
  | 'welcome'
  // ── ONB-5 additions ──
  | 'contact'
  | 'import'
  | 'form'
  | 'passport-goal'
  | 'nova'
  | 'sites'
  | 'expenses'
  | 'nav'
  | 'converge'
  | 'welcome-aboard'
  // ── the original walk ──
  | 'business-type'
  | 'categories'
  | 'aesthetic'
  | 'planets'
  | 'studio'
  | 'label'
  | 'plan'
  | 'project'
  | 'complete';

interface StepDef {
  key: StepKey;
  title: string;
  novaState: NovaState;
  /**
   * ONB-5 — whether this screen offers a way past it. Data on the
   * StepDef rather than a check inside the footer, so the list above is
   * the one place the answer to "can they skip this?" is written, and
   * it reads the same way the spec does.
   */
  skippable?: boolean;
}

// UX-3 — the required walk is FOUR steps: Welcome → Craft → Studio
// setup → done. Every other step below stays in this array (and its
// JSX stays in the render) so its key, its resume-bookmark index and
// HOTFIX-4's `initialStepKey='studio'` lookup all keep working — but
// the routing (see the Continue/Back handlers) skips straight over
// them. Where each deferred step went:
//
//   categories → seeded silently from the craft's service-tab preset
//                (the businessType effect below already does this);
//                editable in Settings, where they already live.
//   aesthetic  → optional "Personalize" link on the complete step,
//                and inline tags on the studio step.
//   planets    → Passport's own first-visit empty state (/galaxy,
//                NovaSuggestedPlanets + PlanetComposer).
//   label      → defaults to 'Studio'; editable in Settings.
//   plan       → Settings → Billing. The free tier exists; the
//                upgrade moment is when a gate fires (UpgradeModal).
//   project    → Today's activation checklist covers it.
//
// Nothing here is deleted — a step that leaves the required path must
// still be reachable where the deferral says it is.
const LEGACY_STEPS: StepDef[] = [
  { key: 'welcome', title: 'Welcome', novaState: 'idle' },
  { key: 'business-type', title: 'What kind of work do you do?', novaState: 'curious' },
  { key: 'categories', title: 'Set up your inquiry categories', novaState: 'thinking' },
  { key: 'aesthetic', title: 'What does your work feel like?', novaState: 'has-suggestion' },
  { key: 'studio', title: 'Set up your business', novaState: 'happy' },
  { key: 'label', title: 'How should we refer to you?', novaState: 'curious' },
  { key: 'plan', title: 'Choose your plan', novaState: 'idle' },
  { key: 'project', title: 'Create your first project', novaState: 'thinking' },
  { key: 'complete', title: "You're in.", novaState: 'celebrating' },
];

/**
 * ONB-5 — the flow as specified. Twelve beats, in order, with the
 * skippable ones marked.
 *
 * The two that cannot be skipped past the first block are deliberate
 * and each for its own reason. Nova is not skippable because a studio
 * who never meets her never uses her, and she is most of what makes
 * Orbit different from a folder of spreadsheets. And the plan is not
 * skippable because the card is how the trial starts.
 *
 * FOUNDERS-GO-3 (Oct 2) — the navigation choice is gone from the walk.
 * Rennan: "kill the aperture bar in the setup, and by default have it
 * set to the rail." Every new account starts on the rail (useNavStyle's
 * default); the Aperture bar is one switch away in Settings →
 * Appearance. The 'nav' entry stays in the array below, retired, only
 * so the step indexes saved as resume bookmarks keep pointing at the
 * same screens — see RETIRED_KEYS.
 *
 * Everything deferred by UX-3 (categories, aesthetic, label, project,
 * the old multi-planet step) stays in the array below the walk: their
 * keys, their resume bookmarks and their Personalize entry points all
 * keep working, and none of them is on the required path.
 */
const ONB5_STEPS: StepDef[] = [
  { key: 'welcome', title: 'Welcome', novaState: 'idle' },
  { key: 'contact', title: 'How do we reach you?', novaState: 'curious' },
  { key: 'business-type', title: 'What kind of work do you do?', novaState: 'curious' },
  { key: 'studio', title: 'Set up your business', novaState: 'happy' },
  { key: 'import', title: 'Bring your people with you', novaState: 'thinking', skippable: true },
  { key: 'form', title: 'Your first form', novaState: 'thinking', skippable: true },
  { key: 'passport-goal', title: 'What are you chasing?', novaState: 'has-suggestion', skippable: true },
  { key: 'nova', title: 'Meet Nova', novaState: 'happy' },
  { key: 'sites', title: 'Sites', novaState: 'has-suggestion', skippable: true },
  { key: 'expenses', title: 'What it costs', novaState: 'thinking', skippable: true },
  // Retired (FOUNDERS-GO-3) — never shown; holds its index. See RETIRED_KEYS.
  { key: 'nav', title: 'How you want to get around', novaState: 'curious' },
  { key: 'converge', title: 'All of it, in one place', novaState: 'celebrating' },
  { key: 'plan', title: 'Choose your plan', novaState: 'idle' },
  { key: 'welcome-aboard', title: 'Welcome aboard', novaState: 'celebrating' },
  // ── Deferred: reachable, never marched through ──
  { key: 'categories', title: 'Set up your inquiry categories', novaState: 'thinking' },
  { key: 'aesthetic', title: 'What does your work feel like?', novaState: 'has-suggestion' },
  { key: 'label', title: 'How should we refer to you?', novaState: 'curious' },
  { key: 'project', title: 'Create your first project', novaState: 'thinking' },
  { key: 'complete', title: "You're in.", novaState: 'celebrating' },
];

const STEPS: StepDef[] = ONB5_FLOW ? ONB5_STEPS : LEGACY_STEPS;


// UX-3 — the required path, in order. Progress display counts over
// THIS list (so the eyebrow reads "03 / 04", not "06 / 10"), and
// stale resume bookmarks pointing at a deferred step get snapped to
// the nearest member.
// SIGNUP-1 — `plan` rejoins the required walk.
//
// UX-3 cut it from this list and deferred billing to Settings →
// Billing. The consequence was the thing Rennan hit: a brand-new
// account walked craft → business → "You're in." and landed on Today
// with no plan, no card, and nothing ever asking for either. Orbit is
// not a free product, so the plan is a required decision, not a
// deferred one.
const LEGACY_REQUIRED_KEYS: StepKey[] = ['welcome', 'business-type', 'studio', 'plan', 'complete'];

/**
 * ONB-5 — every step the walk passes through, skippable or not.
 *
 * The progress counter reads over THIS list, so "07 / 12" is honest
 * even when the studio skips four of them: the bar measures how far
 * through the flow they are, not how much work they chose to do. A
 * skipped step still advances it, which is the behaviour anyone would
 * expect from a bar that was at 50% before they pressed Skip.
 */
const ONB5_REQUIRED_KEYS: StepKey[] = [
  'welcome',
  'contact',
  'business-type',
  'studio',
  'import',
  'form',
  'passport-goal',
  'nova',
  'sites',
  'expenses',
  'converge',
  'plan',
];

const REQUIRED_KEYS: StepKey[] = ONB5_FLOW ? ONB5_REQUIRED_KEYS : LEGACY_REQUIRED_KEYS;

/**
 * FOUNDERS-GO-3 — steps that left the walk but keep their slot in
 * STEPS. The wizard moves by index (Next is `step + 1`) and saves the
 * index as its resume bookmark, so deleting an entry from the middle of
 * the array would shift every saved bookmark after it by one — someone
 * paused on the plan step would come back past it. A retired step is
 * stepped over instead: Next and Back skip it, and a bookmark that
 * points at it resumes on the step after.
 */
const RETIRED_KEYS: ReadonlySet<StepKey> = new Set<StepKey>(['nav']);

/** The nearest index at or beyond `idx`, moving by `dir`, that isn't retired. */
function skipRetired(idx: number, dir: 1 | -1): number {
  let i = idx;
  while (i >= 0 && i < STEPS.length && RETIRED_KEYS.has(STEPS[i].key)) i += dir;
  return Math.min(Math.max(i, 0), STEPS.length - 1);
}

/**
 * ONB-5 — the active walk, for the dev sandbox's step picker.
 *
 * Exported rather than re-declared there, so the picker can never
 * drift from the flow: adding a step here makes it appear in the
 * sandbox with no second edit.
 */
export const ONBOARDING_WALK: ReadonlyArray<{ key: StepKey; title: string; skippable: boolean }> =
  REQUIRED_KEYS.map((key) => {
    const def = STEPS.find((s) => s.key === key);
    return { key, title: def?.title ?? key, skippable: !!def?.skippable };
  });


// Step-indicator marker — small Nova head silhouette.
// We use this in the dotted progress row at the top of every step so Nova
// is the visual through-line of the whole flow.
const NovaHeadDot = ({ active, done }: { active: boolean; done: boolean }) => (
  <div
    className={`relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
      done
        ? 'bg-primary/15 text-primary'
        : active
          ? 'bg-primary text-primary-foreground scale-110 shadow-md shadow-primary/30'
          : 'bg-muted text-muted-foreground'
    }`}
    aria-hidden
  >
    {done ? (
      <OrbitIcon name="checkmark" fallback={Check} className="w-3.5 h-3.5" strokeWidth={3} />
    ) : (
      // Tiny abstract Nova head — antenna + screen
      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none">
        <line x1="8" y1="3" x2="8" y2="6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="8" cy="2.5" r="1" fill="currentColor" />
        <rect x="3" y="6" width="10" height="7" rx="2" fill="currentColor" />
      </svg>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Plan card — uses the mockup's Starter / Studio / Flagship display names
// while the underlying tier IDs stay Stripe-aligned.
// ─────────────────────────────────────────────────────────────────────────────

// PLANS-1B — three-tier lineup mirrors billing.ts.
type DisplayPlan = {
  id: 'orbit_starter' | 'orbit' | 'orbit_business';
  displayName: string;
  price: number;
  description: string;
  features: readonly string[];
  recommended?: boolean;
};

// P0-1 — kept as its own const so the spread below stays type-safe
// and so September is a one-line restore rather than a re-type.
const BUSINESS_DISPLAY_PLAN: DisplayPlan = {
  id: 'orbit_business',
  displayName: 'Orbit Business',
  price: PLAN_PRICING.orbit_business.monthly,
  description: 'Run your team — unlimited seats, production management.',
  features: PLAN_FEATURES.orbit_business,
};

const PLAN_DISPLAY: DisplayPlan[] = [
  {
    id: 'orbit_starter',
    displayName: 'Orbit Starter',
    price: PLAN_PRICING.orbit_starter.monthly,
    description: 'Just starting out — up to 15 clients + 5 active projects.',
    features: PLAN_FEATURES.orbit_starter,
  },
  {
    id: 'orbit',
    displayName: 'Orbit',
    price: PLAN_PRICING.orbit.monthly,
    description: 'Run your business — unlimited clients, custom domain, custom branding.',
    features: PLAN_FEATURES.orbit,
    recommended: true,
  },
  // P0-1 — Business goes on sale with video in mid-September. Until
  // then it isn't offered here: its Stripe prices are placeholders, so
  // picking it and pressing the button could only ever produce a 503
  // on the very first screen a new studio sees.
  ...(BUSINESS_ON_SALE ? [BUSINESS_DISPLAY_PLAN] : []),
];

const PlanCard = ({
  plan,
  selected,
  onSelect,
}: {
  plan: DisplayPlan;
  selected: boolean;
  onSelect: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
        selected
          ? 'border-primary bg-primary/10 shadow-sm shadow-primary/20'
          : 'border-border hover:border-primary/50 hover:bg-muted/30'
      }`}
    >
      {plan.recommended && (
        <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-mustard text-foreground text-[10px] font-semibold rounded-full uppercase tracking-wide">
          Best Value
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground">{plan.displayName}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-xl font-bold text-foreground">${plan.price}</span>
          <span className="text-xs text-muted-foreground">/month</span>
        </div>
      </div>
      <ul className="space-y-1 mt-3">
        {plan.features.slice(0, 5).map((feature, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-foreground">
            <OrbitIcon name="checkmark" fallback={Check} className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {selected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          <OrbitIcon name="checkmark" fallback={Check} className="w-3 h-3" strokeWidth={3} />
        </div>
      )}
    </button>
  );
};

// Debug panel for ?debug=1
const DebugPanel = ({
  step,
  user,
  organization,
  lastError,
  lastPayload,
}: {
  step: number;
  user: any;
  organization: any;
  lastError: string | null;
  lastPayload: any;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 bg-yellow-500/20 text-yellow-500 rounded-full hover:bg-yellow-500/30"
      >
        <OrbitIcon name="ladybug.fill" fallback={Bug} className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className="absolute bottom-12 right-0 w-80 bg-card border border-border rounded-lg p-4 shadow-xl text-xs font-mono">
          <h4 className="font-bold text-foreground mb-2">🔧 Onboarding Debug</h4>
          <div className="space-y-2 text-muted-foreground">
            <p><span className="text-foreground">Step:</span> {step} ({STEPS[step]?.key})</p>
            <p><span className="text-foreground">Logged In:</span> {user ? 'Yes' : 'No'}</p>
            <p><span className="text-foreground">User ID:</span> {user?.id?.slice(0, 8) || 'N/A'}...</p>
            <p><span className="text-foreground">Has Org:</span> {organization ? 'Yes' : 'No'}</p>
            <p><span className="text-foreground">Org ID:</span> {organization?.id?.slice(0, 8) || 'N/A'}...</p>
            {lastError && (
              <p className="text-destructive"><span className="text-foreground">Last Error:</span> {lastError}</p>
            )}
            {lastPayload && (
              <div>
                <span className="text-foreground">Last Payload:</span>
                <pre className="mt-1 p-1 bg-muted rounded text-[10px] overflow-x-auto">
                  {JSON.stringify(lastPayload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * localStorage key used to persist the wizard's current step. If a
 * studio closes the tab mid-flow, we resume them where they left off
 * instead of forcing them through "Welcome" again.
 *
 * Keyed by user id so two studios sharing a browser don't trample
 * each other's progress.
 */
const stepStorageKey = (userId: string | undefined | null) =>
  userId ? `orbit:onboarding-step:${userId}` : 'orbit:onboarding-step:anon';

const OnboardingWizard = ({ onComplete: onCompleteProp, initialStepKey, preview = false }: OnboardingWizardProps) => {
  const dry = preview;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get('debug') === '1';

  const { user, signOut } = useAuth();

  // ONB-IOS-1 — every way out of setup stamps `profiles.onboarded_at`
  // (ONB-5 added the column; nothing wrote it). The iOS app reads it to
  // decide whether an account still owes setup, so a business that set
  // up here isn't asked again there. Best-effort and once: a stamp that
  // already exists is left alone.
  const onComplete = (landingPath?: string) => {
    if (!dry && user?.id) {
      void supabase
        .from('profiles')
        .update({ onboarded_at: new Date().toISOString() })
        .eq('id', user.id)
        .is('onboarded_at', null)
        .then(
          ({ error }) => {
            if (error) debug.warn?.('Failed to stamp onboarded_at', error);
          },
          () => undefined,
        );
    }
    onCompleteProp(landingPath);
  };
  const { data: organization, refetch: refetchOrg, isLoading: orgLoading } = useOrganization();
  const updateOrg = useUpdateOrganization();
  const createOrg = useCreateOrganization();
  // UX-6 — persists the wizard's category picks into inquiry_categories
  // (same delete-then-insert bulk save OrganizationSettings uses), and
  // reads existing rows so a returning studio re-entering the wizard
  // edits their REAL categories instead of clobbering them with presets.
  const saveCategories = useSaveInquiryCategories();
  const { data: existingCategories = [] } = useInquiryCategories();
  const createProject = useCreateProject();
  const createCheckout = useCreateCheckout();
  // LAPSE-1 — ProtectedRoute opens this wizard at 'plan' for any org
  // without a live plan. When that org once PAID, this is not a first
  // visit: it is a studio whose card failed or whose plan ran out, and
  // "choose your plan" reads like we forgot who they are. The plan
  // step reads this and changes its sentence, hides Back (there is no
  // setup behind them to go back to) and hides Skip.
  const lapsedQuery = useLapsedSubscription(organization?.id ?? null);
  const lapsed = initialStepKey === 'plan' && !!lapsedQuery.data?.lapsed;
  const lapsedEnded = lapsedQuery.data?.endedAt
    ? new Date(lapsedQuery.data.endedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;
  const { toast } = useToast();
  const shareOrigin = useShareOrigin();

  // Initialize step from localStorage if a previous session left a
  // bookmark. Falls back to 0 (welcome). Restoring inside useState
  // initializer so we don't flash welcome before the resume kicks in.
  const [step, setStep] = useState<number>(() => {
    // HOTFIX-4 — an explicit initial step beats the resume bookmark.
    // Only ProtectedRoute sends this, and only when it's enforcing
    // org setup on an account that already saw/skipped the intro.
    if (initialStepKey) {
      const idx = STEPS.findIndex((s) => s.key === initialStepKey);
      if (idx >= 0) return skipRetired(idx, 1);
    }
    if (typeof window === 'undefined') return 0;
    try {
      const stored = window.localStorage.getItem(stepStorageKey(user?.id));
      const parsed = stored ? Number.parseInt(stored, 10) : 0;
      // Don't restore the 'complete' step — if they closed there,
      // they should land on Today, not see the celebration again.
      const completeIdx = STEPS.findIndex((s) => s.key === 'complete');
      if (parsed >= completeIdx) return 0;
      if (parsed >= 0 && parsed < STEPS.length) {
        // UX-3 — a bookmark from before the three-step flow can point
        // at a step that left the required path. Snap it: the pre-org
        // steps (categories/aesthetic/planets) resume at 'studio' (the
        // one thing Orbit can't run without), and the post-org tail
        // (label/plan/project) resumes at 'complete' — the org exists,
        // so one click finishes the wizard instead of replaying steps
        // that are now deferred to Settings/Today.
        const bookmarkedKey = STEPS[parsed].key;
        // FOUNDERS-GO-3 — paused on a retired step: resume on the next one.
        if (RETIRED_KEYS.has(bookmarkedKey)) return skipRetired(parsed, 1);
        if (!REQUIRED_KEYS.includes(bookmarkedKey)) {
          const preOrg = ['categories', 'aesthetic', 'planets'].includes(bookmarkedKey);
          const snapIdx = STEPS.findIndex(
            (s) => s.key === (preOrg ? 'studio' : 'complete'),
          );
          return snapIdx >= 0 ? snapIdx : 0;
        }
        return parsed;
      }
    } catch {
      /* localStorage might be disabled in private mode — fine. */
    }
    return 0;
  });
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  // ONB-2 — Rich role picker replaces the coarse BusinessTypeSelector
  // on the business-type step. `creativeRole` drives Nova's
  // illustration choices across the app (Learn cards, empty states,
  // info sheets) and personalizes the guided tour that plays right
  // after the wizard finishes. `businessType` is derived from the
  // picked role (except `other`) and still seeds service-tab presets.
  const [creativeRole, setCreativeRole] = useState<CreativeRole | null>(null);
  const [creativeRoleOther, setCreativeRoleOther] = useState('');
  const [serviceTabs, setServiceTabs] = useState<ServiceTabConfig[]>(getDefaultServiceTabs());
  const [aestheticTags, setAestheticTags] = useState<string[]>([]);
  // ONB-1 — Hoister for the first goal Nova collected during her
  // intro. The intro runs before the wizard, when no org exists yet,
  // so the goal was stashed in localStorage. We land it in the goals
  // table the moment the org is created (below, in an effect that
  // fires when `organization` becomes non-null).
  const createGoal = useCreateGoal();
  const [pendingGoalHoisted, setPendingGoalHoisted] = useState(false);
  // SIGNUP-1 — `'free'` is gone from this union on purpose.
  //
  // Free is where an account LANDS when a trial or a subscription
  // ends. It is not a door you can walk in through, so it is not a
  // signup choice, so it is not a value this state can hold. Deleting
  // it from the TYPE (rather than just hiding the card) is what stops
  // a restored draft or a future deep-link resurrecting the
  // skip-past-billing branch that used to sit in
  // `handleSubscriptionContinue`.
  const [selectedPlan, setSelectedPlan] = useState<'orbit_starter' | 'orbit' | 'orbit_business'>('orbit');

  // FOUNDING-3 — the Founding Pass, for a member who came from their
  // founding page (their private token rides in localStorage). Offered
  // only while their Pass is still buyable: not paid, not sold out, not
  // closed — the page's own read says which.
  //
  // FOUNDERS-GO-4 — and the account remembers it. localStorage is one
  // browser: a founder who signed up on their phone and finished setup
  // on their laptop reached the plan step with no Pass in sight and a
  // plan picker in its place. The wizard copies the intent onto the
  // account (user metadata, below) and reads it back from there when
  // this browser has none.
  const [localIntent] = useState(() => (FOUNDING_PASS.onSale ? readFoundingIntent() : null));
  const accountIntent = useMemo(() => (FOUNDING_PASS.onSale ? foundingIntentOf(user) : null), [user]);
  const foundingIntent = localIntent ?? accountIntent;
  const foundingToken = foundingIntent?.token ?? null;
  const foundingKitQuery = useQuery({
    queryKey: ['founding-kit', foundingToken, 'wizard'],
    queryFn: () => getFoundingKit(foundingToken as string),
    enabled: !!foundingToken,
    staleTime: 60_000,
    retry: 1,
  });
  const foundingKit = foundingKitQuery.data ?? null;
  // FOUNDERS-GO-4 — offered from the moment the intent is known, not
  // from the moment the kit loads: a slow or failed read must not swap
  // a founder's Pass for a plan picker (and a tap on Continue during
  // that read must not open a monthly checkout). Only a kit that SAYS
  // no takes it away — paid, sold out, closed, or a link that matches
  // nobody. founding-pass-checkout checks all of it again before
  // charging anyone.
  const kitSaysNo =
    foundingKitQuery.isSuccess &&
    (!foundingKit ||
      !!foundingKit.pass ||
      foundingKit.passes?.open === false ||
      Date.now() > Date.parse(foundingKit.passes?.closesAt ?? FOUNDING_PASS.closesAt));
  const passOffer = !!foundingToken && !kitSaysNo && Date.now() <= Date.parse(FOUNDING_PASS.closesAt);
  // The kit closed the offer on someone who came for it — the plan step
  // says so instead of silently showing plans.
  const passGone = !!foundingToken && kitSaysNo && !!foundingKit && !foundingKit.pass;
  // Which Pass is picked (the one they chose on their page), or null once
  // they pick a regular plan instead.
  const [passPick, setPassPick] = useState<PassTier | null>(() => foundingIntent?.tier ?? null);
  // The account's copy arrives with `user`, a beat after mount.
  const adoptedIntent = useRef(!!foundingIntent);
  useEffect(() => {
    if (adoptedIntent.current || !foundingIntent) return;
    adoptedIntent.current = true;
    setPassPick((p) => p ?? foundingIntent.tier);
  }, [foundingIntent]);
  const passChosen = passOffer && passPick !== null;
  // FOUNDERS-GO-4 — Rennan: founders who sign up from the email must not
  // be made to "choose a subscription since it already did that". The
  // plan step shows the Pass they picked on their founding page and
  // nothing else; "See other options" opens the full step (both Passes,
  // every plan) for anyone who changed their mind.
  const [showAllPlans, setShowAllPlans] = useState(false);

  // FOUNDERS-GO-4 — copy this browser's founding intent onto the account
  // so it survives a change of browser. Best-effort, once per change.
  useEffect(() => {
    if (dry || !user || !localIntent) return;
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    if (meta.founding_token === localIntent.token && meta.founding_tier === localIntent.tier) return;
    void supabase.auth
      .updateUser({ data: { founding_token: localIntent.token, founding_tier: localIntent.tier } })
      .then(
        ({ error }) => {
          if (error) debug.warn?.('Failed to save the founding intent on the account', error);
        },
        () => undefined,
      );
  }, [dry, user?.id, localIntent]); // eslint-disable-line react-hooks/exhaustive-deps
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [brandColor, setBrandColor] = useState('#D4A02C');
  const [studioLabel, setStudioLabel] = useState('Studio');
  const [savingLabel, setSavingLabel] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);

  // Debug state
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastPayload, setLastPayload] = useState<any>(null);

  // Recovery-candidate confirmation dialog state. Triggered when the
  // signed-in user's email matches an existing studio's intake — we
  // refuse to silently create a duplicate org and ask them to choose.
  const [recoveryCandidate, setRecoveryCandidate] = useState<{
    org_id: string;
    org_name: string | null;
    org_slug: string | null;
  } | null>(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    orgName?: string;
    orgSlug?: string;
    brandColor?: string;
  }>({});

  // Populate form with existing org data
  useEffect(() => {
    if (organization) {
      setOrgName(organization.name || '');
      setOrgSlug(organization.slug || '');
      setBrandColor(organization.brand_color || '#D4A02C');
      if (organization.business_type) {
        setBusinessType(organization.business_type as BusinessType);
      }
      const parsed = parseServiceTabs(organization.service_tabs);
      if (parsed.length > 0) {
        setServiceTabs(parsed);
      }
      const existingLabel = (organization as { studio_label?: string | null }).studio_label;
      if (existingLabel && existingLabel.trim()) {
        setStudioLabel(existingLabel.trim());
      }
      const existingTags = (organization as any).aesthetic_tags as string[] | undefined;
      if (existingTags && existingTags.length > 0) {
        setAestheticTags(existingTags);
      }
    }
  }, [organization]);

  // UX-6 — the inquiry_categories table is the real source of truth
  // (the org's legacy `service_tabs` JSON is usually empty), so when
  // it has rows they win over the preset/legacy seed above. Without
  // this, a studio re-running the wizard would see preset tabs and —
  // now that the wizard actually saves categories — overwrite their
  // real list on the way through.
  useEffect(() => {
    if (existingCategories.length > 0) {
      setServiceTabs(
        existingCategories.map((cat) => ({
          id: cat.name.trim().toLowerCase().replace(/\s+/g, '_'),
          label: cat.name.trim(),
          icon: cat.icon || 'sparkles',
        })),
      );
    }
  }, [existingCategories]);

  // Update service tabs when business type changes.
  //
  // UX-6 — but never let a mere ECHO of the saved business_type
  // (populated from the org row above) stomp the studio's real
  // categories with presets: this effect also fires when the org
  // loads and setBusinessType reflects what's already saved, and the
  // wizard now WRITES serviceTabs back to inquiry_categories on save.
  // Presets only apply when the type actually differs from what's on
  // the org, or when the studio has no categories yet.
  useEffect(() => {
    if (businessType) {
      const isEchoOfSavedType =
        existingCategories.length > 0 &&
        businessType === (organization?.business_type ?? null);
      if (!isEchoOfSavedType) {
        setServiceTabs(serviceTabPresets[businessType]);
      }
    }
  }, [businessType]);

  // Persist current step so a tab close mid-wizard resumes here.
  // Cleared in onComplete handler when the user finishes.
  useEffect(() => {
    if (typeof window === 'undefined' || dry) return;
    try {
      window.localStorage.setItem(stepStorageKey(user?.id), String(step));
    } catch {
      /* private mode — ignore */
    }
  }, [step, user?.id]);

  // FREEWEEK-1 / OPEN-1 — one report per screen shown, and why the plan
  // step is on screen. Mission Control → Early access counts them.
  const trackedStepKey = STEPS[step]?.key;
  useEffect(() => {
    if (dry || !trackedStepKey) return;
    track('setup_step', { step: trackedStepKey });
    if (trackedStepKey === 'plan') {
      track('plan_screen_viewed', { reason: lapsed ? 'lapsed' : passChosen ? 'founder' : 'setup', surface: 'setup' });
    }
  }, [trackedStepKey, dry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ONB-1 — Hoist the first goal Nova captured during her intro
  // (stashed in `orbit_pending_first_goal`). Runs once the org
  // exists — that's the earliest moment the goals table will accept
  // an insert. Idempotent: `pendingGoalHoisted` guards against a
  // second run if the effect re-fires (org refetch, etc.).
  useEffect(() => {
    if (pendingGoalHoisted || dry) return;
    if (typeof window === 'undefined') return;
    const orgId = organization?.id;
    if (!orgId) return;
    let pending: string | null = null;
    try {
      pending = window.localStorage.getItem('orbit_pending_first_goal');
    } catch {
      /* private mode — nothing to hoist */
    }
    if (!pending || pending.trim().length === 0) {
      setPendingGoalHoisted(true);
      return;
    }
    setPendingGoalHoisted(true);
    createGoal
      .mutateAsync({ title: pending.trim(), rings: [{ kind: 'step', label: 'Done' }] })
      .then(() => {
        try {
          window.localStorage.removeItem('orbit_pending_first_goal');
        } catch {
          /* ignore */
        }
      })
      .catch((err) => {
        // Non-blocking: log so we can find failed hoists, but never
        // wedge the wizard on this. The goal is a warm gesture, not
        // a critical account setup step.
        debug.warn?.('Failed to hoist first goal from NovaIntro', err);
      });
  }, [organization?.id, createGoal, pendingGoalHoisted]);

  const currentStep = STEPS[step];
  // UX-3 — progress counts the four required steps only. A deferred
  // step (reachable via Personalize or a stale deep-link) shows the
  // full bar rather than inventing a "06 / 10" numbering for steps
  // the studio will never be marched through.
  const requiredPos = REQUIRED_KEYS.indexOf(currentStep.key);
  const displayStepNumber = requiredPos >= 0 ? requiredPos + 1 : REQUIRED_KEYS.length;
  const progress = (displayStepNumber / REQUIRED_KEYS.length) * 100;

  // Step navigation helpers — all step movement goes through these so the
  // step indices stay decoupled from inline numbers throughout the JSX.
  const goToStepKey = (key: StepKey) => {
    const idx = STEPS.findIndex((s) => s.key === key);
    if (idx >= 0) setStep(idx);
  };
  // FOUNDERS-GO-3 — both step over a retired step (the old navigation chooser).
  const goNext = () => setStep((s) => skipRetired(Math.min(s + 1, STEPS.length - 1), 1));
  const goBack = () => setStep((s) => skipRetired(Math.max(s - 1, 0), -1));

  // SIGNUP-1 — THE RETURN TRIP.
  //
  // `handleSubscriptionContinue` leaves the SPA for Stripe. Coming back
  // is a fresh page load, so nothing about the old React tree survives
  // and the ONLY evidence that anything happened is the query string
  // Stripe was told to return with, plus the subscription row itself.
  //
  // We trust the row, not the query string. `?checkout=success` only
  // tells us Stripe finished its side; `isPaid || isTrial` is the
  // actual answer, and it comes from `check-subscription` reading
  // Stripe. So success re-asks rather than assumes — otherwise a
  // hand-typed `?checkout=success` would be a way through the wall.
  // ── ONB-5 state ────────────────────────────────────────────────
  const createIntakeForm = useCreateIntakeForm();

  const [contactEmail, setContactEmail] = useState(user?.email ?? '');
  const [contactPhone, setContactPhone] = useState('');
  const [contactErrors, setContactErrors] = useState<{ email?: string; phone?: string }>({});

  const [firstFormName, setFirstFormName] = useState('');
  const [formHome, setFormHome] = useState<FormHome>('embed');

  const [goalName, setGoalName] = useState('');
  const [goalMetric, setGoalMetric] = useState<CounterMetric>('bookings');
  // PASSPORT-2 — the goal the wizard set, as the server charted it.
  const [firstGoal, setFirstGoal] = useState<PassportGoal | null>(null);
  const [goalTarget, setGoalTarget] = useState('12');

  const [sitesInterested, setSitesInterested] = useState<boolean | null>(null);
  // SIGNUP-3 — state the redesigned screens need on top of ONB-5's.
  const [passportPhase, setPassportPhase] = useState<'meet' | 'compose' | 'world'>('meet');
  const [goalPicked, setGoalPicked] = useState(false);
  const [goalTargetTouched, setGoalTargetTouched] = useState(false);
  const [orgSlugTouched, setOrgSlugTouched] = useState(false);
  const [planInterval, setPlanInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [importNames, setImportNames] = useState<string[]>([]);
  // CSV-IMPORT-1 — read and HELD for the plan (not in the roster yet).
  const [importHeld, setImportHeld] = useState(false);
  const [seedExpenseCategories, setSeedExpenseCategories] = useState(true);


  // The email field is seeded from the account, but `user` arrives a
  // tick after first render. Seed once, and never stomp on typing.
  const seededEmail = useRef(false);
  useEffect(() => {
    if (seededEmail.current || !user?.email) return;
    seededEmail.current = true;
    setContactEmail((prev) => prev || user.email || '');
  }, [user?.email]);

  const subscription = useSubscription();
  // PAYWALL-1 — server truth only. `isPaid` folds in the demo overlay,
  // which is presentation and must never satisfy the plan wall.
  const hasPlan = subscription.hasServerPlan;
  const checkoutParam = searchParams.get('checkout');

  // SIGNUP-1 — SELF-DISMISS.
  //
  // ProtectedRoute opens this wizard whenever `onboarding_complete`
  // isn't stamped in THIS browser, which includes an established studio
  // signing in on a new laptop. That studio must not be shown a card
  // form. The moment we can see a real plan behind a real org, the
  // wizard stamps and gets out of the way — no screen, no flash.
  const orgIsReal = Boolean(organization?.name && organization?.slug);
  useEffect(() => {
    if (!SIGNUP_REQUIRES_PLAN || dry) return;
    if (checkoutParam) return; // the return-trip effect below owns this
    if (subscription.isLoading || !orgIsReal || !hasPlan) return;
    onComplete();
  }, [orgIsReal, hasPlan, subscription.isLoading, checkoutParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // SIGNUP-1 — the return trip is a ONE-SHOT.
  //
  // Without this ref the effect below is a loop with a toast in it:
  // `refetch()` flips `subscription.isLoading` true then false, that is
  // a dependency, so the effect runs again and calls `refetch()` again,
  // forever. `checkoutParam` never clears on its own — it is in the URL
  // Stripe sent us to — so the early-return can't save us either.
  const checkoutHandled = useRef(false);

  // CHECKOUT-1 — the webhook race is the common case, not the edge.
  // Stripe redirects the browser the instant Checkout completes; the
  // `checkout.session.completed` webhook that writes org_subscriptions
  // usually lands a second or two LATER. One check-subscription call
  // at page load therefore often says "free" for a studio that just
  // paid — and the old one-shot below sent them straight back to the
  // plan wall with a "don't pay twice" toast, which is exactly the
  // moment a nervous studio pays twice. So: ask again, up to six
  // times, 2.5s apart (~15s), before giving up. `confirmTick` is what
  // re-runs the effect when a refetch comes back unchanged.
  const CONFIRM_ATTEMPTS = 6;
  const CONFIRM_DELAY_MS = 2500;
  const confirmAttempts = useRef(0);
  const [confirmTick, setConfirmTick] = useState(0);

  useEffect(() => {
    if (!checkoutParam) return;
    if (checkoutHandled.current) return;

    if (checkoutParam === 'cancelled' || checkoutParam === 'canceled') {
      checkoutHandled.current = true;
      track('checkout_returned', { result: 'cancelled', surface: 'setup' });
      goToStepKey('plan');
      toast({
        title: 'Checkout cancelled',
        description:
          "No card was charged. Pick a plan when you're ready — your business details are saved.",
      });
      return;
    }

    if (checkoutParam !== 'success') return;

    // Ask Stripe again rather than believing the URL. Wait for the
    // answer before deciding anything — `isLoading` is true while
    // `check-subscription` is in flight, and a verdict reached during
    // that window would be reading stale cache.
    if (subscription.isLoading) return;

    if (hasPlan) {
      checkoutHandled.current = true;
      track('checkout_returned', { result: 'success', surface: 'setup' });
      // FOUNDING-3 — whichever they bought, the founding hand-off is done.
      clearFoundingIntent();
      // ONB-5 — the card cleared, so the welcome is what comes next
      // rather than the old summary screen.
      if (ONB5_FLOW) {
        goToStepKey('welcome-aboard');
      } else {
        goToStepKey('complete');
      }
      toast({
        title: "You're subscribed",
        description: `Your ${TRIAL_SHIPPED ? `${TRIAL_DAYS}-day trial has` : 'plan has'} started.`,
      });
      return;
    }

    if (confirmAttempts.current < CONFIRM_ATTEMPTS) {
      // Not confirmed yet — most likely the webhook is still in
      // flight. Wait, ask again, and only then decide.
      if (confirmAttempts.current === 0) {
        toast({
          title: 'Confirming with Stripe…',
          description: 'Your card went through. Just waiting for the confirmation — a few seconds.',
        });
      }
      confirmAttempts.current += 1;
      const timer = window.setTimeout(async () => {
        try {
          await subscription.refetch();
        } finally {
          setConfirmTick((t) => t + 1);
        }
      }, CONFIRM_DELAY_MS);
      return () => window.clearTimeout(timer);
    }

    // Stripe said success; ~15 seconds later our side still disagrees.
    // Say so honestly and leave them on the wall rather than waving
    // through an account we can't confirm.
    checkoutHandled.current = true;
    goToStepKey('plan');
    toast({
      title: 'Still confirming your subscription',
      description:
        "Stripe took the card but we haven't seen the confirmation yet. Give it a minute and refresh — if it persists, contact support. Please don't pay twice.",
    });
    // `subscription` is a fresh object each render; depending on the
    // two booleans we actually read (plus the retry tick) keeps this
    // from looping.
  }, [checkoutParam, hasPlan, subscription.isLoading, confirmTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateOrgForm = (): boolean => {
    const errors: typeof validationErrors = {};

    const trimmedName = orgName.trim();
    if (!trimmedName) {
      errors.orgName = 'Business name is required';
    }

    const slug = orgSlug || trimmedName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (!slug) {
      errors.orgSlug = 'URL slug is required';
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      errors.orgSlug = 'Use only letters, numbers, and hyphens';
    }

    if (brandColor && !/^#[0-9A-Fa-f]{6}$/.test(brandColor)) {
      errors.brandColor = 'Please enter a valid hex color (e.g., #D4A02C)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignIn = () => {
    debug.log('Sign In clicked from onboarding');
    if (user) {
      signOut().then(() => {
        navigate('/auth');
      });
    } else {
      navigate('/auth');
    }
  };

  /**
   * UX-6 — write the wizard's serviceTabs into the `inquiry_categories`
   * table right after the org exists. This is what actually feeds the
   * Add-lead Service dropdown, the pipeline tabs, the Inquiries page,
   * and the public intake form — the org create/update hooks strip
   * `service_tabs` from their payloads, so without this call the
   * categories chosen during onboarding never landed anywhere.
   *
   * Non-blocking on purpose: the org IS saved by the time this runs,
   * so a category failure must not wedge the wizard. The save hook
   * surfaces its own error toast (via debug.parseSupabaseError); we
   * just log and move on — the studio can re-save from Settings, and
   * every consumer falls back to "General Inquiry" until then.
   */
  const persistServiceCategories = async () => {
    try {
      await saveCategories.mutateAsync(
        serviceTabs.map((tab, index) => ({
          name: tab.label,
          icon: tab.icon || 'sparkles',
          color: 'hsl(var(--primary))',
          sort_order: index,
          enabled: true,
        })),
      );
      debug.success('Onboarding: inquiry categories saved', { count: serviceTabs.length });
    } catch (error) {
      debug.error({ action: 'onboarding_save_inquiry_categories', userId: user?.id }, error);
    }
  };

  const handleSaveOrg = async () => {
    if (!validateOrgForm()) {
      return;
    }

    if (!user) {
      toast({
        title: 'Please sign in',
        description: 'You need to sign in to set up your business.',
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }

    if (dry) {
      goToStepKey(ONB5_FLOW ? 'import' : 'plan');
      return;
    }

    setSaving(true);
    setLastError(null);

    const slug = orgSlug || orgName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    const payload = {
      name: orgName.trim(),
      slug,
      brand_color: brandColor || '#D4A02C',
      business_type: businessType,
      service_tabs: serviceTabs,
      // aesthetic_tags is stored on the org once the schema migration lands.
      // For now we hold it in component state and Nova reads it from there.
    };

    setLastPayload(payload);
    debug.log('Onboarding: Saving organization', payload);

    try {
      if (organization) {
        debug.log('Onboarding: Updating existing org', { orgId: organization.id });
        await updateOrg.mutateAsync({
          id: organization.id,
          ...payload,
        });
        debug.success('Onboarding: Organization updated');
      } else {
        debug.log('Onboarding: Creating new organization');
        const newOrg = await createOrg.mutateAsync(payload);
        debug.success('Onboarding: Organization created', { orgId: newOrg.id });
        setCreatedOrgId(newOrg.id);
        setOrgSlug(slug);
        await refetchOrg();
      }

      // UX-6 — land the category picks in inquiry_categories now that
      // the org row exists (the org hooks drop `service_tabs`).
      await persistServiceCategories();

      toast({
        title: 'Business saved',
        description: 'Your business settings have been saved.',
      });
      // SIGNUP-1 — the org is the last thing we ASK for; the plan is
      // the last thing we REQUIRE. This was `goToStepKey('complete')`,
      // which is what made "finish setup" and "have an account" the
      // same moment regardless of billing. Label (defaults to
      // 'Studio') and first project (Today's activation checklist)
      // stay deferred — they always were optional.
      // ONB-5 — the walk continues into the optional block; under the
      // legacy flow this is still the jump straight to the wall.
      goToStepKey(ONB5_FLOW ? 'import' : 'plan');
    } catch (error: unknown) {
      // Recovery candidate guard — show confirmation UI, do not auto-bind.
      if (error instanceof OrgRecoveryCandidateError) {
        debug.warn?.('Recovery candidate intercepted in onboarding', error.candidate);
        setRecoveryCandidate(error.candidate);
        setSaving(false);
        return;
      }
      const err = error as Error;
      debug.error({ action: 'onboarding_save_org', userId: user?.id }, err);
      const errorMessage = err?.message || 'Unknown error';
      setLastError(errorMessage);

      // SIGNUP-HOTFIX — THE MATCHER NEVER MATCHED, AND THE DEFAULT ATE
      // THE ANSWER.
      //
      // `errorMessage` here is `parseOrgError`'s OUTPUT, not the raw
      // Postgres message — organizations.ts has already turned 42501
      // into "Permission denied. Please sign in again or contact
      // support." and 23505 into "This URL slug is already in use."
      // Neither string contains the lowercase 'permission', 'policy',
      // 'duplicate' or 'unique' these tests were looking for, so EVERY
      // real cause fell past all three branches and rendered as the
      // generic default. That is why signup failures all looked
      // identical from the outside.
      //
      // Two fixes, both required: match case-insensitively, and make
      // the default the message we actually have rather than a
      // placeholder that discards it.
      const haystack = errorMessage.toLowerCase();
      let userMessage = errorMessage || 'Failed to save organization. Please try again.';

      if (haystack.includes('slug') || haystack.includes('duplicate') || haystack.includes('unique')) {
        setValidationErrors((prev) => ({ ...prev, orgSlug: 'This URL slug is already in use. Please choose a different one.' }));
      } else if (haystack.includes('permission') || haystack.includes('policy') || haystack.includes('42501')) {
        userMessage = `Your session isn't allowed to create a business — sign out and back in, then try again. (${errorMessage})`;
      } else if (haystack.includes('network') || haystack.includes('fetch')) {
        userMessage = 'Network error — please check your connection and try again.';
      }

      toast({
        title: 'Error saving business',
        description: userMessage,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubscriptionContinue = async () => {
    if (dry) {
      // Preview: pretend the card cleared.
      goToStepKey(ONB5_FLOW ? 'welcome-aboard' : 'complete');
      return;
    }
    // SIGNUP-1 — the `selectedPlan === 'free'` branch that used to open
    // this function jumped straight past billing into the rest of the
    // wizard. It was the widest of the four ways an account could
    // finish setup without a card ever being asked for.

    // FOUNDING-3 — the Founding Pass instead of a plan. Same exit as a
    // plan: leave for Stripe and come back to `/?checkout=success`,
    // where the return trip below re-asks for the subscription.
    if (passChosen && foundingToken && passPick) {
      setSaving(true);
      track('checkout_started', { pass: passPick, surface: 'setup' });
      try {
        const result = await startFoundingPass(foundingToken, passPick, { returnPath: '/?checkout=success' });
        if (result.kind === 'redirect') {
          window.location.href = result.url;
          return;
        }
        // Already paid, or an existing subscription to switch — neither
        // belongs in setup. The founding page handles both.
        clearFoundingIntent();
        if (result.kind === 'done') {
          window.location.href = '/?checkout=success';
          return;
        }
        navigate(`/founding/${foundingToken}`);
      } catch (error) {
        const code = error instanceof PassCheckoutError ? error.code : null;
        if (code === 'sold_out' || code === 'pass_closed' || code === 'member_has_pass') {
          setPassPick(null);
          void foundingKitQuery.refetch();
        }
        toast({
          title: code === 'sold_out' ? 'The Founding Passes are gone' : 'Could not open checkout',
          description:
            error instanceof Error
              ? `${error.message}${code === 'sold_out' || code === 'pass_closed' ? ' Pick a plan to carry on.' : ''}`
              : 'Stripe is unreachable. Try again in a moment.',
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // P0-1 — `selectedPlan` defaults to 'orbit' and the only way to
    // change it is tapping a card in PLAN_DISPLAY, which no longer
    // contains Business while it's held. Guarded anyway so a restored
    // draft or a future deep-link can't push a studio into a checkout
    // that answers 503.
    if (selectedPlan === 'orbit_business' && !BUSINESS_ON_SALE) {
      toast({
        title: "That plan isn't on sale yet",
        description: `${BUSINESS_HELD_MESSAGE} Pick Orbit or Orbit Starter for now — you can move up later.`,
      });
      return;
    }

    setSaving(true);
    track('checkout_started', { plan: selectedPlan, surface: 'setup' });
    try {
      const url = await createCheckout.mutateAsync({
        plan: selectedPlan,
        interval: 'monthly',
        // SIGNUP-1 — come back HERE, not to Settings → Billing. The
        // wizard is mid-flight; the default destination would strand a
        // studio who just paid on a page that knows nothing about it.
        returnPath: '/?checkout=success',
      });
      if (!url) {
        // Defensive: the mutation resolved without a URL. Treat as
        // failure rather than silently advancing — otherwise the
        // studio thinks they're trialing while no subscription row
        // exists, then hits a paywall later with no idea what happened.
        throw new Error('Stripe did not return a checkout URL.');
      }
      // SIGNUP-1 — SAME TAB, AND THE WIZARD DOES NOT ADVANCE.
      //
      // This was `window.open(url, '_blank')` followed immediately by
      // `goToStepKey('project')`. The wizard walked on the instant
      // Stripe was *opened*, with no idea whether a card was ever
      // entered — close the Stripe tab and you had a finished-looking
      // account with no subscription behind it.
      //
      // Leaving the SPA entirely is the point. The only way back into
      // onboarding is Stripe's own return URL, so a studio cannot be
      // past this step and unpaid at the same time. The `?checkout=`
      // parameter on the way back is what the return effect reads.
      // Subscription always runs on web, never through Apple IAP, so
      // the iOS app links here too.
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      // Surface the real failure instead of silently advancing. Trial
      // users don't get a subscription row when checkout fails, so
      // pretending everything's fine sets them up to be confused
      // when billing later denies a feature. Keep them on this step
      // with a Skip-and-continue affordance via the Skip button below.
      toast({
        title: 'Could not start checkout',
        description:
          error instanceof Error
            ? `${error.message} You can try again, or click "Skip for now" to continue without subscribing.`
            : 'Stripe is unreachable. Try again, or skip and subscribe later from Settings.',
        variant: 'destructive',
      });
      setLastError(
        error instanceof Error
          ? error.message
          : 'Stripe checkout failed for an unknown reason.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ══ ONB-5 · the writes ═══════════════════════════════════════════
  //
  // One rule across all of them: NOTHING in this flow may strand a
  // studio who has already paid, or is about to. A failed side-effect
  // warns and advances. The only screen allowed to hold someone in
  // place is the one collecting something Orbit genuinely cannot work
  // without, which is the contact step and the plan.

  /**
   * ONB-5 — one-time warning latch for the profile columns.
   * `profiles.phone` / `profiles.onboarded_at` arrive with
   * 20260917120000_onb5_profile_contact.sql. Same shape as the GAL-7
   * and GAL-14 fallbacks: degrade, loudly, once.
   */
  const warnedMissingProfileColumns = useRef(false);

  const handleContactContinue = async () => {
    const email = contactEmail.trim();
    const phone = contactPhone.trim();
    const errs: { email?: string; phone?: string } = {};
    if (!isPlausibleEmail(email)) errs.email = 'That doesn’t look like an email address.';
    if (!isPlausiblePhone(phone)) errs.phone = 'That doesn’t look like enough digits for a phone number.';
    setContactErrors(errs);
    if (errs.email || errs.phone) return;

    // Saved best-effort. The studio typed it; making them retype it
    // because a column is missing would be our problem becoming theirs.
    if (user?.id && !dry) {
      const { error } = await supabase
        .from('profiles')
        .update({ email, phone } as never)
        .eq('id', user.id);
      if (error) {
        const code = (error as { code?: string }).code;
        if (code === 'PGRST204' && !warnedMissingProfileColumns.current) {
          warnedMissingProfileColumns.current = true;
          console.warn(
            '[ONB-5] profiles.phone does not exist on this database — ' +
              'supabase/migrations/20260917120000_onb5_profile_contact.sql has not been applied. ' +
              'The phone number was collected and NOT stored.',
          );
        } else {
          debug.error({ action: 'onboarding_save_contact', userId: user.id }, error as Error);
        }
      }
    }
    goNext();
  };

  const handleFormContinue = async () => {
    const name = firstFormName.trim() || 'Tell me about your day';
    if (dry) {
      goNext();
      return;
    }
    try {
      await createIntakeForm.mutateAsync({
        name,
        description: null,
        category_id: null,
        slug: '',
        is_default: true,
        is_active: true,
        sections: [],
        // The builder's own defaults fill these in; onboarding only
        // decides the name and where it will be pointed.
        theme_config: { accentColor: brandColor } as never,
        settings: { autoCreateProject: true, sendConfirmationEmail: true } as never,
      } as never);
      toast({
        title: 'Form created',
        description:
          formHome === 'embed'
            ? "Your embed code is in Settings → Intake forms when you're ready for it."
            : 'Your hosted link is in Settings → Intake forms.',
      });
    } catch (error: unknown) {
      debug.error({ action: 'onboarding_create_form', userId: user?.id }, error as Error);
      toast({
        title: "Couldn't create that form",
        description: 'You can set one up any time in Settings → Intake forms.',
      });
    }
    goNext();
  };

  // SIGNUP-3 — the CSV import, owned here so the screen can show the
  // names coming across and a progress capsule while it runs.
  //
  // CSV-IMPORT-1 (Oct 2) — Rennan: "CSV is failing in the setup". It sent
  // the rows to crm-import in a shape that function never understood
  // ("Unknown op"), so no file ever came across; and this step comes
  // before the plan step, where the business is still on Free — 3
  // clients, enforced by the database. Now the file is read here
  // (quotes, semicolons, BOMs, first + last names), and:
  //   - with a plan already live (a comped business, setup re-run), the
  //     clients go straight into the roster;
  //   - otherwise they're HELD on this device and come across the
  //     moment the plan starts (PendingClientImport, beside this wizard),
  //     dated now, so a "New clients" goal set later doesn't count them.
  const handleImportFile = async (file: File) => {
    setImporting(true);
    setImportProgress(12);
    try {
      if (file.size > MAX_IMPORT_BYTES) {
        throw new ClientImportError("That file is over 10 MB. Export just your contacts and try again.");
      }
      const read = readClientsCsv(await file.text());
      const clients = read.clients;
      setImportNames(clients.slice(0, 3).map((c) => c.name));
      setImportProgress(55);
      const extra = [
        read.duplicates ? `${read.duplicates} duplicate${read.duplicates === 1 ? '' : 's'} left out` : '',
        read.blank ? `${read.blank} row${read.blank === 1 ? '' : 's'} with no name or email skipped` : '',
        read.truncated ? `only the first ${clients.length} taken` : '',
      ].filter(Boolean).join(' · ');

      if (dry) {
        await new Promise((r) => setTimeout(r, 900));
        setImportHeld(true);
        setImportedCount(clients.length);
        setImportProgress(100);
        return;
      }

      if ((hasPlan || !SIGNUP_REQUIRES_PLAN) && organization?.id) {
        const r = await importClients(organization.id, clients);
        if (r.leftover.length && user?.id) {
          savePendingImport(user.id, {
            v: 1,
            pickedAt: new Date().toISOString(),
            fileName: file.name,
            total: clients.length,
            clients: r.leftover,
          });
        }
        setImportHeld(false);
        setImportedCount(r.imported);
        setImportProgress(100);
        toast({
          title: `Brought in ${r.imported} client${r.imported === 1 ? '' : 's'}.`,
          description:
            [
              r.alreadyThere ? `${r.alreadyThere} already in your roster` : '',
              r.leftover.length ? `${r.leftover.length} more come across when your plan has room` : '',
              extra,
            ]
              .filter(Boolean)
              .join(' · ') || undefined,
        });
        return;
      }

      if (!user?.id) throw new ClientImportError("You're signed out — sign in and pick the file again.");
      // Dated before setup's first goal, even when they come Back here
      // after setting it, so it never counts these as new.
      const goalAt = firstGoal?.created_at ? Date.parse(firstGoal.created_at) - 1000 : Number.POSITIVE_INFINITY;
      const pickedAt = new Date(Math.min(Date.now(), goalAt)).toISOString();
      const held = savePendingImport(user.id, { v: 1, pickedAt, fileName: file.name, total: clients.length, clients });
      if (!held) {
        throw new ClientImportError(
          "This browser won't hold the file for us (private browsing?). You can import it from Settings → Import once you're set up.",
        );
      }
      setImportHeld(true);
      setImportedCount(clients.length);
      setImportProgress(100);
      if (extra) toast({ title: `${clients.length} client${clients.length === 1 ? '' : 's'} ready.`, description: extra });
    } catch (err) {
      toast({
        title: "That file didn't come across",
        description: err instanceof ClientImportError ? err.message : `Something went wrong reading it. ${(err as Error)?.message ?? ''}`.trim(),
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  // CSV-IMPORT-1 — "Use a different file": let go of the held one.
  const handleImportReset = () => {
    if (user?.id && importHeld) clearPendingImport(user.id);
    setImportHeld(false);
    setImportedCount(null);
    setImportNames([]);
    setImportProgress(0);
  };

  // CSV-IMPORT-1 — coming back to setup (a reload, Back from a later
  // step, the trip to Stripe): a held file is still held.
  const restoredHeld = useRef(false);
  useEffect(() => {
    if (restoredHeld.current || dry || !user?.id) return;
    restoredHeld.current = true;
    const pending = loadPendingImport(user.id);
    if (!pending) return;
    setImportHeld(true);
    setImportedCount(pending.clients.length);
    setImportNames(pending.clients.slice(0, 3).map((c) => c.name));
  }, [user?.id, dry]);

  // SIGNUP-3 — "Make it a world": save the goal, then show it drifting
  // to the edge of the galaxy before moving on. PASSPORT-2 — the goal
  // is one sentence with exactly one counter ring ("12 bookings" is
  // one ring, not three); the server charts it on the next free world
  // and the World stage shows the one it chose.
  const handleGoalMakeWorld = async () => {
    const G = GOALS[goalMetric as keyof typeof GOALS];
    const name = goalName.trim() || G?.sug || 'This year';
    if (!goalName.trim()) setGoalName(name);
    const target = Number(String(goalTarget).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(target) || target <= 0) {
      goNext();
      return;
    }
    if (dry) {
      setPassportPhase('world');
      return;
    }
    try {
      const goal = await createGoal.mutateAsync({
        title: name,
        rings: [{ kind: 'counter', metric: goalMetric, target: Math.round(target) }],
      });
      setFirstGoal(goal);
      setPassportPhase('world');
    } catch (error: unknown) {
      debug.error({ action: 'onboarding_create_goal', userId: user?.id }, error as Error);
      toast({ title: "Couldn't save that goal", description: 'You can add it in Passport whenever you like.' });
      goNext();
    }
  };

  const handleCreateProject = async () => {
    const orgId = organization?.id || createdOrgId;
    if (!orgId || !projectName) {
      goToStepKey('complete');
      return;
    }

    setSaving(true);
    setLastError(null);

    const payload = {
      name: projectName,
      org_id: orgId,
      status: 'discovery' as const,
    };

    setLastPayload(payload);
    debug.log('Onboarding: Creating project', payload);

    try {
      await createProject.mutateAsync(payload);
      debug.success('Onboarding: Project created', { name: projectName });
      toast({
        title: 'Project created',
        description: `${projectName} has been added to your workspace.`,
      });
      goToStepKey('complete');
    } catch (error: unknown) {
      const err = error as Error;
      debug.error({ action: 'onboarding_create_project', userId: user?.id }, err);
      setLastError(err?.message || 'Unknown error');
      toast({
        title: 'Error creating project',
        description: 'Failed to create project. You can create one later from the dashboard.',
        variant: 'destructive',
      });
      goToStepKey('complete');
    } finally {
      setSaving(false);
    }
  };

  // BRAND-2 — resolved rather than hard-coded, even though at this point
  // in onboarding a custom domain almost never exists yet, so this is
  // usually just the app's own origin. It matters for the case where one
  // DOES exist (an existing studio replaying the wizard), and it keeps
  // this snippet built the same way as the one in Settings.
  //
  // What this cannot fix: the host is baked into the snippet the moment
  // it is pasted onto the studio's site, and nobody revisits it. A domain
  // added later never reaches it. A real fix is a loader script that
  // resolves the host per page load instead of an iframe with a literal
  // src — worth doing if white-label embeds become load-bearing. Until
  // then the copy below points at Settings, which regenerates it.
  const embedCode = `<iframe
  src="${shareOrigin}/embed/intake/${orgSlug || organization?.slug || 'your-org'}"
  width="100%"
  height="800"
  frameborder="0"
  style="border: none; border-radius: 12px;"
></iframe>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Embed code copied' });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  // ONB-4 — Skip handler. Stamp `onboarding_complete` so ProtectedRoute
  // doesn't bounce them back into the wizard, then hand off to
  // NovaIntro (or straight to Today if they've already met Nova).
  //
  // HOTFIX-4 — Skip bypasses the tour steps, never org creation.
  // Stamping `onboarding_complete` with no org row was the hole that
  // let a fresh account Skip its way onto Today, where every write
  // dies with "Organization not found". If there's no valid org yet,
  // Skip now routes INTO the "Set up your business" step instead of
  // out of the wizard, and the flag is NOT stamped.
  const handleSkipWizard = () => {
    if (dry) {
      onComplete('/');
      return;
    }
    const hasOrg = Boolean(
      (organization?.name && organization?.slug) || createdOrgId,
    );
    if (!hasOrg) {
      toast({
        title: 'One quick thing first',
        description:
          "Your business name and URL are the one step Orbit can't run without. Set those up — everything after is skippable.",
      });
      goToStepKey('studio');
      return;
    }
    // SIGNUP-1 — Skip stops at the plan wall for the same reason
    // HOTFIX-4 made it stop at org creation: it stamps
    // `onboarding_complete`, and that stamp is what ProtectedRoute
    // reads to decide the account is ready. Letting it fire from
    // anywhere upstream of billing would reopen the hole the rest of
    // this change closes — just through a different button.
    if (SIGNUP_REQUIRES_PLAN && !hasPlan) {
      toast({
        title: 'One more thing',
        description:
          'Orbit needs a plan before your account opens. Everything you set up so far is saved.',
      });
      goToStepKey('plan');
      return;
    }
    try {
      syncStorage.set('onboarding_complete', 'true');
      window.localStorage.removeItem(stepStorageKey(user?.id));
    } catch {
      /* private mode — ignore */
    }
    const introSeen =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('orbit_nova_intro_seen') === '1';
    navigate(introSeen ? '/' : '/nova-intro');
  };

  // SIGNUP-3 — every required step is a first-run screen
  // (FirstRunSteps / PassportStages). The deferred "Personalize" steps
  // keep their existing JSX inside a glass panel on the same frame.
  const craft = craftOf(creativeRole);
  const craftAccent = CRAFT[craft].acc;
  const stepCount = `${String(displayStepNumber).padStart(2, '0')} / ${String(REQUIRED_KEYS.length).padStart(2, '0')}`;
  const isRequired = REQUIRED_KEYS.includes(currentStep.key);
  const count = isRequired ? stepCount : 'Optional';
  const firstName = (user?.user_metadata?.full_name as string | undefined)?.trim().split(/\s+/)[0];
  const studioName = organization?.name ?? orgName;
  const nav = (o: Partial<StepNav> = {}): StepNav => ({
    onNext: goNext,
    onBack: goBack,
    count,
    accent: craftAccent,
    palette: currentStep.key,
    onSkipAll: lapsed ? undefined : handleSkipWizard,
    ...o,
  });
  const chrome = (
    <>
      {showDebug && (
        <DebugPanel step={step} user={user} organization={organization} lastError={lastError} lastPayload={lastPayload} />
      )}
      <AlertDialog
        open={!!recoveryCandidate}
        onOpenChange={(open) => { if (!open) setRecoveryCandidate(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Existing business found</AlertDialogTitle>
            <AlertDialogDescription>
              We found a business
              {recoveryCandidate?.org_name ? <> called <strong>{recoveryCandidate.org_name}</strong></> : null}
              {' '}already linked to <strong>{user?.email}</strong>.
              If this is yours, sign in to recover it instead of creating a
              duplicate. If someone entered your email by mistake, you can
              continue and create a new business.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setRecoveryCandidate(null);
                navigate('/auth');
              }}
            >
              Sign in to recover
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const slug = orgSlug || orgName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                const payload = {
                  name: orgName.trim(),
                  slug,
                  brand_color: brandColor || '#D4A02C',
                  business_type: businessType,
                  service_tabs: serviceTabs,
                  allowDuplicate: true,
                };
                setRecoveryCandidate(null);
                setSaving(true);
                try {
                  const newOrg = await createOrg.mutateAsync(payload);
                  setCreatedOrgId(newOrg.id);
                  setOrgSlug(slug);
                  await refetchOrg();
                  // UX-6 — same category persistence as handleSaveOrg.
                  await persistServiceCategories();
                  toast({ title: 'Business saved', description: 'Your business settings have been saved.' });
                  // UX-3 — same routing as handleSaveOrg: org saved
                  // means the required path is done.
                  goToStepKey('complete');
                } catch (e) {
                  const err = e as Error;
                  setLastError(err?.message || 'Unknown error');
                  toast({
                    title: 'Error saving business',
                    description: err?.message || 'Failed to save organization.',
                    variant: 'destructive',
                  });
                } finally {
                  setSaving(false);
                }
              }}
            >
              Create new business anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  let screen: React.ReactNode = null;
  switch (currentStep.key) {
    case 'welcome':
      screen = <WelcomeStage nav={nav({ palette: 's1', onBack: undefined })} firstName={firstName} studioName={studioName} />;
      break;
    case 'contact':
      screen = (
        <ContactStage
          nav={nav({ palette: 's2', onNext: () => void handleContactContinue() })}
          email={contactEmail}
          phone={contactPhone}
          onEmail={(v) => { setContactEmail(v); if (contactErrors.email) setContactErrors((p) => ({ ...p, email: undefined })); }}
          onPhone={(v) => { setContactPhone(v); if (contactErrors.phone) setContactErrors((p) => ({ ...p, phone: undefined })); }}
          errors={contactErrors}
        />
      );
      break;
    case 'business-type':
      screen = (
        <CraftStage
          nav={nav({
            palette: craft,
            onNext: async () => {
              const persistValue = creativeRolePersistValue(creativeRole, creativeRoleOther);
              if (persistValue && user?.id && !dry) {
                try {
                  await (supabase.from('profiles') as any).update({ creative_role: persistValue }).eq('id', user.id);
                } catch (err) {
                  debug.warn?.('Failed to persist creative_role', err);
                }
              }
              goToStepKey('studio');
            },
          })}
          role={creativeRole}
          onRole={(role) => {
            setCreativeRole(role);
            const card = ROLE_CARDS.find((c) => c.role === role);
            if (card?.businessType) setBusinessType(card.businessType);
          }}
          otherText={creativeRoleOther}
          onOtherText={setCreativeRoleOther}
        />
      );
      break;
    case 'studio':
      screen = (
        <StudioStage
          nav={nav({ palette: 's4', onNext: () => void handleSaveOrg(), onBack: () => goToStepKey('business-type'), busy: saving })}
          name={orgName}
          slug={orgSlug}
          onName={(v) => {
            setOrgName(v);
            setValidationErrors((prev) => ({ ...prev, orgName: undefined }));
            if (!orgSlugTouched) setOrgSlug(v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
          }}
          onSlug={(v) => {
            setOrgSlugTouched(true);
            setOrgSlug(v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
            setValidationErrors((prev) => ({ ...prev, orgSlug: undefined }));
          }}
          errors={validationErrors}
          origin={shareOrigin}
        />
      );
      break;
    case 'import':
      screen = (
        <ImportStage
          nav={nav({ palette: 's5', onSkip: goNext })}
          importing={importing}
          imported={importedCount}
          held={importHeld}
          names={importNames}
          progress={importProgress}
          onFile={(file) => void handleImportFile(file)}
          onReset={handleImportReset}
        />
      );
      break;
    case 'form':
      screen = (
        <FormStage
          nav={nav({ palette: 's6', onNext: () => void handleFormContinue(), onSkip: goNext, busy: createIntakeForm.isPending })}
          craft={craft}
          formName={firstFormName}
          home={formHome}
          onName={setFirstFormName}
          onHome={setFormHome}
          studioName={studioName}
        />
      );
      break;
    case 'passport-goal':
      if (passportPhase === 'meet') {
        screen = <PassportMeetStage nav={nav({ onNext: () => setPassportPhase('compose'), onSkip: goNext })} />;
      } else if (passportPhase === 'compose') {
        screen = (
          <PassportComposeStage
            nav={nav({ onNext: () => void handleGoalMakeWorld(), onSkip: goNext, onBack: () => setPassportPhase('meet'), busy: createGoal.isPending })}
            metric={goalMetric}
            target={goalTarget}
            name={goalName}
            picked={goalPicked}
            onMetric={(m) => { setGoalMetric(m); setGoalPicked(true); if (!goalTargetTouched) setGoalTarget(GOALS[m as keyof typeof GOALS]?.def ?? goalTarget); }}
            onTarget={(v) => { setGoalTargetTouched(true); setGoalTarget(v); }}
            onName={setGoalName}
          />
        );
      } else {
        screen = <PassportWorldStage nav={nav({ onNext: goNext })} metric={goalMetric} name={goalName} studioName={studioName} world={firstGoal ? worldOf(firstGoal) : null} />;
      }
      break;
    case 'nova':
      screen = <NovaStage nav={nav({ palette: 's8' })} />;
      break;
    case 'sites':
      screen = (
        <SitesStage
          nav={nav({ palette: 's9', onSkip: goNext })}
          interested={sitesInterested}
          onInterested={setSitesInterested}
          slug={orgSlug}
          studioName={studioName}
          craftLabel={CRAFT[craft].label}
          origin={shareOrigin}
        />
      );
      break;
    case 'expenses':
      screen = (
        <ExpensesStage
          nav={nav({ palette: 's10', onSkip: goNext })}
          seed={seedExpenseCategories}
          onSeed={setSeedExpenseCategories}
          categories={STARTER_EXPENSE_CATEGORIES}
        />
      );
      break;
    // FOUNDERS-GO-3 — 'nav' (the Aperture bar / rail chooser) is retired:
    // Next and Back step over it, so it has no screen.
    case 'converge':
      screen = (
        <ConvergeStage
          nav={nav({
            palette: 's12',
            onNext: () => {
              track('setup_finished', { founder: passChosen });
              goNext();
            },
          })}
          craftLabel={creativeRole ? getRoleCard(creativeRole).title : CRAFT[craft].label}
          importedCount={importedCount}
          formName={firstFormName}
          studioName={studioName}
          goalText={goalPicked ? `${goalName || GOALS[goalMetric as keyof typeof GOALS]?.sug || ''} · ${goalText(goalMetric, goalTarget)}` : null}
          siteOn={sitesInterested}
          slug={orgSlug}
          // FOUNDERS-GO-4 — a founder has nothing to choose next.
          cta={passChosen ? 'Continue' : undefined}
        />
      );
      break;
    case 'plan':
      screen = (
        <PlanStage
          nav={nav({ palette: 's13', onNext: () => void handleSubscriptionContinue(), onBack: lapsed ? undefined : goBack, busy: saving, count: lapsed ? 'Welcome back' : count })}
          plans={PLAN_DISPLAY.map((p) => p.id)}
          selected={passChosen ? null : selectedPlan}
          onSelect={(p) => {
            setPassPick(null);
            setSelectedPlan(p);
          }}
          // FOUNDERS-GO-4 — the founder's Pass alone, until they ask for more.
          passOnly={passChosen && !showAllPlans && !lapsed}
          onSeeOptions={() => setShowAllPlans(true)}
          passGone={passGone && !lapsed}
          foundingPass={
            passOffer
              ? {
                  options: FOUNDING_PASS.order.map((t) => {
                    const n = passNumbers(t);
                    return { tier: t, name: n.plan, price: n.price, renewal: n.renewal };
                  }),
                  selected: passChosen ? passPick : null,
                  onSelect: setPassPick,
                  years: FOUNDING_PASS.years,
                  afterPct: FOUNDING_PASS.afterPct,
                }
              : undefined
          }
          interval={planInterval}
          onInterval={setPlanInterval}
          suggested={importedCount || craft === 'film' || craft === 'both' ? 'orbit' : 'orbit_starter'}
          lapsed={lapsed}
          lapsedEnded={lapsedEnded}
          onSignOut={handleSignIn}
        />
      );
      break;
    case 'welcome-aboard':
      screen = (
        <WelcomeAboardStage
          nav={nav({ palette: 's14' })}
          firstName={firstName}
          studioName={studioName}
          planetName={firstGoal ? worldOf(firstGoal).name : undefined}
          onEnter={() => {
            try {
              window.localStorage.removeItem(stepStorageKey(user?.id));
            } catch {
              /* ignore */
            }
            // SIGNUP-4 — the Orbit tour plays once, then opens Today.
            onComplete(ORBIT_TOUR_PATH);
          }}
          // SIGNUP-8 — or not: the tour is offered, not imposed. It
          // lives in Learn for whenever they want it.
          onSkipTour={() => {
            try {
              window.localStorage.removeItem(stepStorageKey(user?.id));
            } catch {
              /* ignore */
            }
            onComplete('/');
          }}
        />
      );
      break;
    default:
      // Deferred / Personalize steps — unchanged JSX on the same frame.
      screen = (
        <FirstRunFrame
          palette="s12"
          accent={craftAccent}
          onBack={goBack}
          stage={
            <div data-a="block" className="w-full max-w-2xl rounded-[28px] bg-white/[0.08] p-6 text-[#F2EFEB] backdrop-blur-2xl md:p-8" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.16)' }}>
              <h2 className="font-editorial mb-6 text-3xl leading-[0.95]">{currentStep.title}</h2>
          {currentStep.key === 'categories' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 px-1">
                <NovaExpression id="thinking" size={64} className="shrink-0" />
                <p className="text-sm text-muted-foreground pt-3">
                  These appear as filters in your Forms dashboard. You can change them later.
                </p>
              </div>

              <ServiceTabsEditor tabs={serviceTabs} onChange={setServiceTabs} />

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={goNext} disabled={serviceTabs.length === 0} className="flex-1">
                  Continue
                  <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
          {currentStep.key === 'aesthetic' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 px-1">
                <NovaExpression id="suggestion" size={64} className="shrink-0" />
                <p className="text-sm text-muted-foreground pt-3">
                  Pick up to 3 vibes. This shapes Nova's suggestions and your default theme.
                </p>
              </div>

              <AestheticPicker value={aestheticTags} onChange={setAestheticTags} />

              {/* UX-3 — this step left the required path; it's only
                  reachable from the "Personalize" link on the complete
                  step, so both exits return there instead of walking
                  the retired 10-step chain (goNext used to lead to
                  planets). */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => goToStepKey('complete')} className="flex-1">
                  <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => goToStepKey('complete')} disabled={aestheticTags.length === 0} className="flex-1">
                  Done
                  <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
          {currentStep.key === 'label' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 px-1">
                <NovaExpression id="curious" size={64} className="shrink-0" />
                <p className="text-sm text-muted-foreground pt-3">
                  Whatever you pick shows up on your inquiry forms, client portals, and emails.
                </p>
              </div>

              <div className="space-y-3">
                <Label>Quick picks</Label>
                <div className="flex flex-wrap gap-2">
                  {Array.from(
                    new Set(
                      [
                        'Business',
                        orgName.trim(),
                        (user?.user_metadata?.full_name as string | undefined)?.trim() || '',
                      ].filter(Boolean),
                    ),
                  ).map((option) => {
                    const active = studioLabel.trim() === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setStudioLabel(option)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-muted-foreground border-border hover:border-primary/40'
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="studio-label">Or type something custom</Label>
                <Input
                  id="studio-label"
                  value={studioLabel}
                  onChange={(e) => setStudioLabel(e.target.value)}
                  placeholder="Business"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={async () => {
                    if (!organization) {
                      goToStepKey('plan');
                      return;
                    }
                    setSavingLabel(true);
                    try {
                      await updateOrg.mutateAsync({
                        id: organization.id,
                        studio_label: studioLabel.trim() || 'Studio',
                      } as Parameters<typeof updateOrg.mutateAsync>[0]);
                      goToStepKey('plan');
                    } catch (error: unknown) {
                      const err = error as Error;
                      debug.error({ action: 'onboarding_save_label', orgId: organization.id }, err);
                      toast({
                        title: "Couldn't save",
                        description: err?.message || 'Please try again.',
                        variant: 'destructive',
                      });
                    } finally {
                      setSavingLabel(false);
                    }
                  }}
                  disabled={!studioLabel.trim() || savingLabel}
                  className="flex-1"
                >
                  {savingLabel ? (
                    <>
                      <NovaSpinner size={16} className="mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          {currentStep.key === 'project' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 px-1">
                <NovaExpression id="thinking" size={64} className="shrink-0" />
                <p className="text-sm text-muted-foreground pt-3">
                  Add the first project so Today has something to greet you with. Or skip — you can add one anytime.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-name">Project name</Label>
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., Brand Campaign Video"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleCreateProject} disabled={saving} className="flex-1">
                  {saving ? (
                    <>
                      <NovaSpinner size={16} className="mr-2" />
                      Creating...
                    </>
                  ) : projectName ? (
                    <>
                      Create project
                      <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Skip
                      <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
              {/* Explicit Skip link — the primary button label flips to
                  "Skip" when projectName is empty, but that's only
                  discoverable by accident. Make it obvious. */}
              <button
                type="button"
                onClick={() => {
                  setProjectName('');
                  goToStepKey('complete');
                }}
                className="block mx-auto text-xs text-muted-foreground/70 hover:text-foreground underline-offset-2 hover:underline"
              >
                Skip — I'll create a project later
              </button>
            </div>
          )}
          {currentStep.key === 'complete' && (
            <div className="space-y-6 text-center">
              <div className="flex flex-col items-center gap-3">
                <NovaExpression id="celebrating" size={120} />
                <div>
                  {/* #494 Pass 6 — Complete-step editorial hero.
                      Was `text-3xl font-display` (upright sans);
                      swapped to `.font-editorial` at 4xl to match
                      the rest of the wizard's step titles. */}
                  <h2 className="font-editorial text-4xl md:text-5xl text-foreground leading-tight">You're in.</h2>
                  <p className="text-muted-foreground mt-2">
                    Welcome aboard{user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name}` : ''}.
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your business is ready. Let's go make something.
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-left">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Embed code for your website
                </Label>
                <div className="relative">
                  <pre className="bg-muted rounded-lg p-3 text-[11px] overflow-x-auto">
                    <code className="text-foreground">{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={copyEmbedCode}
                  >
                    {copied ? (
                      <>
                        <OrbitIcon name="checkmark" fallback={Check} className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <OrbitIcon name="doc.on.doc" fallback={Copy} className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Paste on your website to capture inquiries directly into Orbit.
                  If you add a custom domain later, re-copy it from Settings →
                  Intake forms.
                </p>
              </div>

              <Button
                onClick={() => {
                  // PAYWALL-1 — 'complete' is reachable from the
                  // recovery dialog, handleCreateProject and the
                  // deferred steps, none of which pass the plan step.
                  // This button was the one exit that stamped without
                  // asking. Same wall as Skip.
                  if (SIGNUP_REQUIRES_PLAN && !hasPlan) {
                    toast({
                      title: 'One more thing',
                      description:
                        'Orbit needs a plan before your account opens. Everything you set up so far is saved.',
                    });
                    goToStepKey('plan');
                    return;
                  }
                  // Clear the resume bookmark — the studio is done with
                  // the wizard. Without this, signing out and back in
                  // would resurrect the wizard at 'complete'.
                  try {
                    window.localStorage.removeItem(stepStorageKey(user?.id));
                  } catch {
                    /* ignore */
                  }
                  onComplete();
                }}
                className="w-full"
                size="lg"
              >
                Open Orbit
                <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 ml-2" />
              </Button>

              {/* UX-3 — the single optional "Personalize" entry point.
                  The aesthetic (vibes) step kept its code but left the
                  required walk; this is where it stays reachable, and
                  both of its exits come back here. Everything else that
                  moved out of the wizard has its own home: categories
                  in Settings, goals in Passport, plan in Settings →
                  Billing, first project on Today's checklist. */}
              <button
                type="button"
                onClick={() => goToStepKey('aesthetic')}
                className="block mx-auto text-xs text-muted-foreground/70 hover:text-foreground underline-offset-2 hover:underline"
              >
                Personalize — pick your studio's vibe (optional)
              </button>
            </div>
          )}
            </div>
          }
          text={<div />}
          cta={<div />}
        />
      );
  }

  return (
    <div data-theme="dark" data-theme-lock="dark" style={{ color: '#F2EFEB' }}>
      {chrome}
      {screen}
    </div>
  );
};



export default OnboardingWizard;
